import { AfterViewInit, Component, ElementRef, inject, OnInit, Input, computed, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { MatBottomSheetRef, MatBottomSheet } from '@angular/material/bottom-sheet';
import { IconComponentV2 } from "../../shared/components/icon-component/icon.component";
import { ApiService } from '../../core/services/api.service';
import { MenuItem, MENU_ITEMS } from '../sidebar/menu.config';
import { MobileSheetComponent } from '../../shared/components/mobile-sheet/mobile-sheet.component';
import { CommonService } from '../../core/services/common.service';

@Component({
  selector: 'app-mobile-nav-sheet',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponentV2],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    @if (mode === 'bar') {
     
      <div class="fixed left-0 right-0 bottom-0 z-[6000] flex flex-col pointer-events-none will-change-transform">

        <!-- Active Child Selection Card for mobile screen in bottom of the screen -->
        @if (activeChild(); as child) {
          @if (activeParent()?.children && activeParent()!.children!.length > 1) {
            <div class="px-6 pb-4 transition-transform duration-500 ease-in-out"
                 [class.translate-y-[82px]]="!showNav() && showFloatingBar()"
                 [class.translate-y-[500px]]="!showFloatingBar()">
             
            <div (click)="openChildMenu()"
                class="pointer-events-auto bg-white dark:bg-surface-1 border border-gray-100 dark:border-line rounded-2xl p-4 flex items-center gap-3 shadow-[0_15px_35px_rgba(0,0,0,0.1)] dark:shadow-[0_15px_35px_rgba(0,0,0,0.4)] animate-in fade-in slide-in-from-bottom-2 duration-300 active:scale-95 cursor-pointer">
                <app-icon-v2 [name]="activeParent()?.icon || 'fileText'" [size]="20" class="text-purple-600 dark:text-purple-300"></app-icon-v2>
                <span class="flex-1 font-bold text-[15px] text-[#141437] dark:text-white line-clamp-1">{{ child.label }}</span>
                <app-icon-v2 name="chevronUp" [size]="20" class="text-gray-400 dark:text-ink-soft"></app-icon-v2>
              </div>
            </div>
          }
        }

        <!-- Parent Navigation Bar (Full Width) at bottom of the screen -->
         
        <div #navBar class="pointer-events-auto bg-white dark:bg-surface-1 border-t border-gray-100 dark:border-line rounded-t-[32px] px-2 py-4 flex items-center justify-around shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.2)] dark:shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.6)] transition-transform duration-500 ease-in-out [transform:translateZ(0)] will-change-transform" style="padding-bottom: calc(env(safe-area-inset-bottom) + 20px);" [class.translate-y-full]="!showNav()">
          @for (item of navDisplay().visible; track item.label) {
            <button (click)="navigateToParent(item.label)" 
              class="flex flex-col items-center gap-1.5 px-3 transition-all active:scale-95 relative">
              <app-icon-v2 [name]="item.icon || 'layoutDashboard'" [size]="20" 
                [class]="isParentActive(item.label) ? 'text-purple-600 dark:text-purple-300' : 'text-gray-400 dark:text-ink-soft'"></app-icon-v2>
              <span class="text-[9px] font-black uppercase tracking-[0.05em]"
                [class]="isParentActive(item.label) ? 'text-purple-600 dark:text-purple-300' : 'text-gray-400 dark:text-ink-soft'">{{ item.label.split(' ')[0] }}</span>
             @if (isParentActive(item.label)) {
                <div class="absolute -bottom-2 w-1.5 h-1.5 bg-purple-600 rounded-full shadow-[0_0_8px_rgba(168,85,247,0.8)]"></div>
              }
            </button>
          }

          @if (navDisplay().hasMore) {
            <button (click)="openMore()" 
              class="flex flex-col items-center gap-1.5 px-3 transition-all active:scale-95">
              <app-icon-v2 name="circle-ellipsis" [size]="24" class="text-gray-400 dark:text-ink-soft"></app-icon-v2>
              <span class="text-[9px] font-black uppercase tracking-[0.05em] text-gray-400 dark:text-ink-soft">More</span>
            </button>
          }
        </div>
      </div>
    } @else {
      <div class="bg-white dark:bg-surface-1 h-full flex flex-col">
        <div class="flex flex-col p-4">
          @for (item of displayItems(); track item.label) {
            <div class="space-y-1 mb-2">
              @if (!filterParentLabel) {
                <!-- Full Menu Mode: Parent Items -->
                <div (click)="handleItemClick(item)"
                  class="flex items-center gap-3 p-4 rounded-2xl transition-all active:scale-[0.98] cursor-pointer"
                  [ngClass]="isParentActive(item.label) ? 'bg-purple-50 dark:bg-white/10 text-purple-600 dark:text-white shadow-sm' : 'text-gray-600 dark:text-ink-soft hover:bg-gray-50 dark:hover:bg-white/5'">
                  <app-icon-v2 [name]="item.icon || 'layoutDashboard'" [size]="20"></app-icon-v2>
                  <span class="flex-1 font-bold text-base">{{ item.label }}</span>
                  @if (!isMoreMode && item.children && item.children.length > 0) {
                    <app-icon-v2 [name]="item.expanded ? 'chevronUp' : 'chevronDown'" [size]="18" class="text-gray-400 dark:text-ink-soft"></app-icon-v2>
                  }
                </div>

                @if (!isMoreMode && item.expanded && item.children) {
                  <div class="pl-12 pr-4 py-2 space-y-1 animate-in fade-in slide-in-from-top-1 duration-200">
                    @for (child of item.children; track child.label) {
                      @if (!child.hidden) {
                        <div (click)="navigateTo(child.route)"
                          class="p-3 rounded-xl text-sm font-medium transition-all active:scale-[0.98] cursor-pointer flex items-center justify-between gap-2"
                          [ngClass]="isChildActive(child) ? 'text-purple-600 dark:text-white bg-purple-50/50 dark:bg-white/10' : 'text-gray-500 dark:text-ink-soft hover:bg-gray-50 dark:hover:bg-white/5'">
                          <span class="flex-1">{{ child.label }}</span>
                          @if (isChildActive(child)) {
                            <app-icon-v2 name="chevronRight" [size]="16" class="text-purple-600 dark:text-white"></app-icon-v2>
                          }
                        </div>
                      }
                    }
                  </div>
                }
              } @else {
                <!-- Filtered Mode: Direct Child Items -->
                <div (click)="navigateTo(item.route)"
                  class="flex items-center gap-3 p-4 rounded-2xl transition-all active:scale-[0.98] cursor-pointer"
                  [ngClass]="isChildActive(item) ? 'bg-purple-50 dark:bg-white/10 text-purple-600 dark:text-white shadow-sm' : 'text-gray-600 dark:text-ink-soft hover:bg-gray-50 dark:hover:bg-white/5'">
                  <span class="flex-1 font-bold text-base">{{ item.label }}</span>
                  @if (isChildActive(item)) {
                    <app-icon-v2 name="chevronRight" [size]="18" class="text-purple-600 dark:text-white"></app-icon-v2>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
    }
  `
})
export class MobileNavSheetComponent implements OnInit, AfterViewInit {
  @Input() mode: 'bar' | 'sheet' = 'sheet';
  @Input() filterParentLabel?: string;
  @Input() isMoreMode = false;

  @ViewChild('navBar') navBarRef?: ElementRef<HTMLElement>;
  private resizeObserver?: ResizeObserver;

  private bottomSheetRef = inject(MatBottomSheetRef<any>, { optional: true });
  private bottomSheet = inject(MatBottomSheet);
  router = inject(Router);
  private apiService = inject(ApiService);
  private commonService = inject(CommonService);

  showNav = this.commonService.showMobileNav;
  showFloatingBar = this.commonService.showMobileFloatingBar;

  menuItems = computed(() => {
    // Make this reactive to product selection and dynamic routes
    const productId = this.apiService.selectedProductId();
    const stored = sessionStorage.getItem('dynamic_routes');
    if (!stored || !productId) return [];

    const allProducts = JSON.parse(stored) as any[];
    const selectedProduct = allProducts.find((p: any) => p.product?.id === productId);

    if (!selectedProduct?.routes?.length) return [];

    const toAppRoute = (path: string): string => {
      if (!path) return '/app';
      return path.startsWith('/app') ? path : '/app/' + path.replace(/^\/+/, '');
    };

    return selectedProduct.routes
      .filter((item: any) => item.onlyPath === false)
      .map((item: any) => {
        const staticMenu = MENU_ITEMS.find((m: MenuItem) => m.label.toLowerCase() === item.name.toLowerCase());
        const children = (item.subMenus || []).map((sub: any) => ({
          label: sub.name,
          route: sub.path ? toAppRoute(sub.path) : null,
          hidden: sub.onlyPath === true,
        }));

        const route = item.path ? toAppRoute(item.path) : null;
        return {
          label: item.name,
          route,
          icon: item?.icon || staticMenu?.icon || 'layoutDashboard',
          children,
        };
      });
  });
// mobile bottom navigation bar display items
  navDisplay = computed(() => {
    const all = this.menuItems();
    if (all.length >= 5) {
      return {
        visible: all.slice(0, 4),
        hasMore: true
      };
    }
    return {
      visible: all,
      hasMore: false
    };
  });

  currentUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)
    )
  );

  activeParent = computed(() => {
    const url = this.currentUrl() || '';
    const items = this.menuItems();
    return items.find((m:any) => 
      (m.route && url === m.route) || 
      m.children?.some((c:any) => c.route && (url === c.route || url.startsWith(c.route + '/'))) ||
      (m.route && url.startsWith(m.route + '/'))
    );
  });

  activeChild = computed(() => {
    const url = this.currentUrl() || '';
    const parent = this.activeParent();
    return parent?.children?.find((c:any) => 
      c.route && (url === c.route || url.startsWith(c.route + '/') || url.startsWith(c.route + '?'))
    );
  });

  displayItems = computed(() => {
    const all = this.menuItems();
    if (this.filterParentLabel) {
      const parent = all.find((m:any) => m.label.toLowerCase() === this.filterParentLabel!.toLowerCase());
      return parent?.children || [];
    }
    if (this.isMoreMode) {
      return all.slice(4);
    }
    return all;
  });

ngOnInit() {
    window.visualViewport?.addEventListener(
      'resize',
      this.handleViewportChange
    );
}

  ngAfterViewInit() {
    // must not overwrite it.
    if (this.mode !== 'bar' || !this.navBarRef) return;

    this.publishNavHeight();
    // Re-measure after fonts/layout settle (helps slower Android browsers
    // where the first measurement happens before safe-area paddings apply).
    requestAnimationFrame(() => this.publishNavHeight());
    setTimeout(() => this.publishNavHeight(), 300);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.publishNavHeight());
      this.resizeObserver.observe(this.navBarRef.nativeElement);
    } else {
      window.addEventListener('resize', this.publishNavHeight);
    }

   
    window.addEventListener('orientationchange', this.publishNavHeight);
  }

ngOnDestroy() {
    window.visualViewport?.removeEventListener(
      'resize',
      this.handleViewportChange
    );

    if (this.mode === 'bar') {
      this.resizeObserver?.disconnect();
      this.resizeObserver = undefined;
      window.removeEventListener('resize', this.publishNavHeight);
      window.removeEventListener('orientationchange', this.publishNavHeight);
      // Reset the variable so any leftover layout doesn't keep the gap.
      document.documentElement.style.removeProperty('--mobile-nav-height');
    }
  }
 
  private publishNavHeight = (): void => {
    const el = this.navBarRef?.nativeElement;
    if (!el) return;
    const height = Math.round(el.getBoundingClientRect().height);
    document.documentElement.style.setProperty(
      '--mobile-nav-height',
      `${height}px`
    );
  };
  handleViewportChange = () => {
    document.documentElement.style.setProperty(
      '--vvh',
      `${window.visualViewport?.height || window.innerHeight}px`
    );
  };
  
  isParentActive(label: string): boolean {
    const url = this.currentUrl() || '';
    const parent = this.menuItems().find((m:any) => m.label.toLowerCase() === label.toLowerCase());
    if (!parent) return false;
    
    return !!(
      (parent.route && url.startsWith(parent.route)) || 
      parent.children?.some((c:any) => c.route && (url === c.route || url.startsWith(c.route + '/')))
    );
  } 
  isChildActive(child: any): boolean {
    const url = this.currentUrl() || '';
    if (!child?.route) return false;
    return url === child.route
      || url.startsWith(child.route + '/')
      || url.startsWith(child.route + '?');
  }

  navigateToParent(label: string) {
    const parent = this.menuItems().find((m:any) => m.label.toLowerCase() === label.toLowerCase());
    if (!parent) return;

    if (parent.route) {
      this.navigateTo(parent.route);
    } else if (parent.children?.length) {
      this.navigateTo(parent.children[0].route);
    }
  }

  handleItemClick(item: any) {
    if (this.filterParentLabel) {
      // In child menu mode, clicking the header does nothing
      return;
    }

    if (this.isMoreMode) {
      if (item.route) {
        this.navigateTo(item.route);
      } else if (item.children?.length) {
        this.navigateTo(item.children[0].route);
      }
      return;
    }

    if (item.children && item.children.length > 0) {
      // Toggle expansion locally if needed
      item.expanded = !item.expanded;
    } else if (item.route) {
      this.navigateTo(item.route);
    }
  }

  navigateTo(route: string | undefined) {
    if (route) {
      this.router.navigateByUrl(route);
      this.close();
    }
  }

  openChildMenu() {
    const active = this.activeParent();
    if (!active) return;

    this.bottomSheet.open(MobileSheetComponent, {
      data: {
        component: MobileNavSheetComponent,
        data: { 
          mode: 'sheet',
          filterParentLabel: active.label
        },
        title: active.label,
        icon: active.icon
      },
      panelClass: 'mobile-sheet-wrapper-container'
    });
  }

  openMore() {
    this.bottomSheet.open(MobileSheetComponent, {
      data: {
        component: MobileNavSheetComponent,
        data: { 
          mode: 'sheet',
          isMoreMode: true 
        },
        title: 'Main Menu',
        icon: 'menu'
      },
      panelClass: 'mobile-sheet-wrapper-container'
    });
  }

  close() {
    if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss();
    }
  }
}

